<template>
  <div
    class="cb-link-raw flex items-center w-full"
    :class="{
      'cb-link-raw--text-link': data?.preview_type === LinkPreviewTypes.TEXT_LINK,
      'pa-2': isEditMode && !hasPadding(style),
      'justify-center': data?.alignment === LinkAlignmentTypes.CENTER,
      'justify-start': data?.alignment === LinkAlignmentTypes.LEFT,
      'justify-end': data?.alignment === LinkAlignmentTypes.RIGHT,
    }"
    :style="style"
  >
    <CButton
      class="max-w[100%]"
      :class="{ 'cursor-not-allowed': isEditMode || (!data?.url && !data?.canvas_anchor) }"
      :style="getStyleByPreviewType(data)"
      :type="data?.preview_type === 'solid-button' ? 'primary' : 'quaternary'"
      @click="handleNavigation(data)"
    >
      {{ data?.display_text }}
    </CButton>
  </div>
</template>

<script setup lang="ts">
import { omit } from 'lodash-es'
import { inject } from 'vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import {
  LinkAlignmentTypes,
  LinkPreviewTypes,
  type CanvasBuilderProps,
  type ComponentNode,
  type LinkProps,
  type FileOpenParams,
  LinkAnchorTypes,
} from '@canvas-builder/types/canvas'

import CButton from '@lib/components/CButton/CButton.vue'

import { prefixHttps } from '@lib/util/string'
import { hasPadding } from '@lib/util/style.util'

defineProps<{
  id?: ComponentNode['id']
  data?: LinkProps
  style?: ComponentNode['style']
}>()

const openExternalUrl = inject<CanvasBuilderProps['onOpenExternalUrl']>('openExternalUrl')

const { isEditMode, onFileOpen } = useCanvas()
const { goToPage } = useCanvasPages()

function getStyleByPreviewType(data: LinkProps | undefined) {
  if (!data) return {}

  const sharedStyles = {
    fontFamily: data?.font_family,
    fontSize: `${data?.font_size}px`,
    height: 'auto',
    lineHeight: '2em',
  }

  switch (data.preview_type) {
    case LinkPreviewTypes.SOLID_BUTTON:
      return {
        ...sharedStyles,
        '--n-border': data?.background_color,
        backgroundColor: data?.background_color,
        color: data?.color,
      }
    case LinkPreviewTypes.OUTLINE_BUTTON:
      return {
        ...sharedStyles,
        border: `1px solid ${data?.color}`,
        backgroundColor: 'transparent',
        color: data?.color,
      }
    case LinkPreviewTypes.TEXT_LINK:
      return {
        ...sharedStyles,
        backgroundColor: 'transparent',
        color: data?.color,
      }
    default: {
      return {}
    }
  }
}

function handleNavigation(data: LinkProps | undefined) {
  if (!data || isEditMode.value) return
  if (data.link_type === LinkAnchorTypes.WEB && data.url && data.target)
    openExternalUrl?.({ url: prefixHttps(data.url), target: data.target })
  else if (data.link_type === LinkAnchorTypes.CANVAS_ANCHOR && data.canvas_anchor)
    goToPage({ componentId: data.canvas_anchor, scrollTo: 'component' })
  else if (data.link_type === LinkAnchorTypes.FILE && data.file) onFileClick(data.file, data.page_index)
}

function onFileClick(file: LinkProps['file'], page_index?: LinkProps['page_index']) {
  if (!file) return

  const payload = {
    newTab: false,
    source_file_id: file.id,
    page_index,
    ...omit(file, 'id'),
  } as FileOpenParams

  payload.file_id = file.id ?? ''
  onFileOpen.value?.(payload)
}
</script>

<style scoped lang="scss">
.cb-link-raw :deep(.n-button__content) {
  display: block;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.cb-link-raw--text-link :deep(.n-button__content) {
  text-overflow: unset;
  overflow: auto;
  white-space: wrap;
}
</style>
